import { useMutation } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { fetchSearchProductData } from "../api/searchProduct.api";
import { fetchCompanyFullProductData } from "../api/fullProductData.api";
import { fetchCompanyFullModelData } from "../api/fullModelData.api";
import { Family } from "../api/models/fullProductData.models";
import { RowValues } from "../api/models/rowValues.models";

export interface RefreshedProductData {
  name: string;
  ean: string;
  active: boolean;
  rowValues: RowValues[];
}

interface UseRefreshReportRowProps {
  companyId: number;
}

const useRefreshReportRow = ({ companyId }: UseRefreshReportRowProps) => {
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken]),
  );

  const fetchRefreshedProductData = async (
    ean: string,
  ): Promise<RefreshedProductData | undefined> => {
    const searchResult = await fetchSearchProductData(
      bearerToken as string,
      companyId,
      true,
      undefined,
      [ean],
      undefined,
      true,
    );

    const family = searchResult?.data?.[0]?.family;

    if (!family) {
      return undefined;
    }

    if (family === Family.product) {
      const result = await fetchCompanyFullProductData(
        bearerToken as string,
        companyId,
        [ean],
        true,
      );
      const product = result.data?.[0];

      if (!product) {
        return undefined;
      }

      return {
        name: product.product.name,
        ean: (product.product.ean ||
          product.product.internalReference) as string,
        active: product.product.responsibleToReport ?? true,
        rowValues: product.packageRows?.rowValues || [],
      };
    }

    const result = await fetchCompanyFullModelData(
      bearerToken as string,
      companyId,
      [ean],
      false,
    );
    const model = result.data?.[0];

    if (!model) {
      return undefined;
    }

    return {
      name: model.model.name,
      ean: (model.model.ean || model.model.internalReference) as string,
      active: model.model.responsibleToReport ?? true,
      rowValues: model.packageRows?.rowValues || [],
    };
  };

  const refreshRowMutation = useMutation({
    mutationFn: fetchRefreshedProductData,
  });

  return {
    refreshRow: refreshRowMutation.mutateAsync,
    isRefreshingRow: refreshRowMutation.isPending,
  };
};

export default useRefreshReportRow;
